import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { fetchApiGet } from "../utils";

// Base schemas for nested objects
export const LocationSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  alias: z.string().nullable(),
  link: z.string().nullable(),
});

export const ServiceCategorySchema = z.object({
  id: z.string().uuid(),
  service_name: z.string(),
  service_group: z.string(),
});

export const FetchRunSchema = z.object({
  id: z.string().uuid(),
  created_at: z.coerce.date(),
  updated_at: z.coerce.date(),
  run_started_at: z.coerce.date(),
  run_completed_at: z.coerce.date().nullable(),
  location_id: z.string().uuid(),
  job_id: z.string().uuid().nullable(),
  prompt_version: z.string(),
  run_status: z.string(),
  error_message: z.string().nullable(),
  raw_html_content_id: z.string().uuid().nullable(),
  minimized_html_content_id: z.string().uuid().nullable(),
  cleaned_content_id: z.string().uuid().nullable(),
  mapped_content_id: z.string().uuid().nullable(),
  diff_content_id: z.string().uuid().nullable(),
  raw_hour_ids: z.array(z.string()),
  changed_hour_ids: z.array(z.string()),
  has_been_alerted: z.boolean().nullable(),
  meta_data: z.record(z.unknown()),
  location: LocationSchema,
});

export const RawHoursSchema = z.object({
  id: z.string().uuid(),
  created_at: z.coerce.date(),
  updated_at: z.coerce.date(),
  location_id: z.string().uuid(),
  service_category_id: z.string().uuid().nullable(),
  date: z.coerce.date(),
  status: z.string(),
  open_time: z.string().nullable(),
  close_time: z.string().nullable(),
  closure_reason: z.string().nullable(),
  grade_note: z.string().nullable(),
  instructions_note: z.string().nullable(),
  global_note: z.string().nullable(),
  other_note: z.string().nullable(),
  fetch_run_id: z.string().uuid(),
  data_source: z.string(),
  is_baseline_derived: z.boolean(),
  content_hash: z.string(),
  meta_data: z.record(z.unknown()),
  location: LocationSchema,
  serviceCategory: ServiceCategorySchema.nullable(),
});

export const ActualHoursSchema = z.object({
  id: z.string().uuid(),
  created_at: z.coerce.date(),
  updated_at: z.coerce.date(),
  location_id: z.string().uuid(),
  service_category_id: z.string().uuid().nullable(),
  date: z.coerce.date(),
  status: z.string(),
  open_time: z.string().nullable(),
  close_time: z.string().nullable(),
  closure_reason: z.string().nullable(),
  grade_note: z.string().nullable(),
  instructions_note: z.string().nullable(),
  global_note: z.string().nullable(),
  other_note: z.string().nullable(),
  fetch_run_id: z.string().uuid(),
  data_source: z.string(),
  is_baseline_derived: z.boolean(),
  content_hash: z.string(),
  meta_data: z.record(z.unknown()),
  location: LocationSchema,
  serviceCategory: ServiceCategorySchema.nullable(),
});

export const RunContentSummarySchema = z.object({
  rawHoursCount: z.number().int().nonnegative(),
  actualHoursCount: z.number().int().nonnegative(),
  totalRecords: z.number().int().nonnegative(),
});

// Request schemas
export const GetRunContentParamsSchema = z.object({
  runId: z.string().uuid(),
});

export const GetRunContentQuerySchema = z.object({
  service_category_id: z.string().uuid().optional(),
  limit: z.coerce.number().min(1).max(1000).optional().default(500),
});

// Response schemas
export const GetRunContentResponseDataSchema = z.object({
  run: FetchRunSchema,
  rawHours: z.array(RawHoursSchema),
  actualHours: z.array(ActualHoursSchema),
  previousRun: z.object({
    run: FetchRunSchema,
    rawHours: z.array(RawHoursSchema),
    actualHours: z.array(ActualHoursSchema),
  }).optional(),
  summary: RunContentSummarySchema,
});

export const GetRunContentResponseSchema = z.object({
  message: z.string(),
  data: GetRunContentResponseDataSchema,
});

// TypeScript interfaces derived from schemas
export type Location = z.infer<typeof LocationSchema>;
export type ServiceCategory = z.infer<typeof ServiceCategorySchema>;
export type FetchRun = z.infer<typeof FetchRunSchema>;
export type RawHours = z.infer<typeof RawHoursSchema>;
export type ActualHours = z.infer<typeof ActualHoursSchema>;
export type RunContentSummary = z.infer<typeof RunContentSummarySchema>;

export type GetRunContentParams = z.infer<typeof GetRunContentParamsSchema>;
export type GetRunContentQuery = z.infer<typeof GetRunContentQuerySchema>;
export type GetRunContentResponseData = z.infer<typeof GetRunContentResponseDataSchema>;
export type GetRunContentResponse = z.infer<typeof GetRunContentResponseSchema>;

// Helper function for query parameter assembly
function assembleGetRunContentQueryParams(filters?: Partial<GetRunContentQuery>) {
  const queryParams = new URLSearchParams();
  if (!filters) {
    return queryParams;
  }

  if (filters.service_category_id) {
    queryParams.append("service_category_id", filters.service_category_id);
  }

  if (filters.limit !== undefined) {
    queryParams.append("limit", filters.limit.toString());
  }

  return queryParams;
}

// React Query Hook
export function useGetRunContent(runId: string, filters?: Partial<GetRunContentQuery>) {
  const queryParams = assembleGetRunContentQueryParams(filters);

  return useQuery({
    queryKey: ["destinationHoursImproved", "runContent", runId, queryParams.toString()],
    queryFn: () => fetchApiGet<GetRunContentResponseData>(`destination-hours-improved/runs/${runId}/content`, queryParams),
    retry: 1,
    enabled: !!runId, // Only run if runId is provided
  });
}
